import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  CardMedia,
  CircularProgress,
  Typography,
  Avatar,
  useMediaQuery,
  useTheme,
  IconButton
} from '@mui/material';
import FavoriteIcon from "@mui/icons-material/Favorite";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination, Navigation } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

import { Fancybox } from "@fancyapps/ui";
import "@fancyapps/ui/dist/fancybox/fancybox.css";


const ImageReplaceWithPicker = ({ photos = [], profileImage, isOnLine,isDeletedAccount }) => {
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [image, setImage] = useState(profileImage || "/assets/images/user-profile/fancy-images/img1.jpg");
  const getStatus = (status) => {
    switch (status) {
      case 'online':
        return { label: 'Online', color: '#00c853' };
      case 'offline':
        return { label: 'Offline', color: '#d32f2f' };
      case 'recently':
        return { label: 'Recently Active', color: '#ffab00' };
      default:
        return { label: 'Unknown', color: '#9e9e9e' };
    }
  };

  const userStatus = isOnLine ? 'online' : 'offline'; // this should come from user object
  const { label, color } = getStatus(userStatus);

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setIsUploading(true);
    setUploadProgress(0);

    const interval = setInterval(() => {
      setUploadProgress((prev) => {
        if (prev >= 100) {
          clearInterval(interval);
          const reader = new FileReader();
          reader.onload = (e) => {
            setImage(e.target.result);
            setIsUploading(false);
          };
          reader.readAsDataURL(file);
        }
        return prev + 10;
      });
    }, 150);
  };

  // 🔄 Bind Fancybox after every render where image/slide updates
  useEffect(() => {
    Fancybox.destroy();
    Fancybox.bind('[data-fancybox="gallery"]', {
      groupAll: true,
      hideScrollbar: false,
      Thumbs: false,
    });
    return () => Fancybox.destroy();
  }, [image, photos]);

  const [favorites, setFavorites] = useState([]);
  const handleToggleFavorite = (key) => {
    setFavorites((prev) =>
      prev.includes(key) ? prev.filter((i) => i !== key) : [...prev, key]
    );
  };

  return (
    <>
      <Box>
        <Box className="swiprslider"
          sx={{
            position: 'relative',
            width: { xs: '100%', sm: '100%', md: '410px' },
            height: { xs: '60vh', sm: '400px', md: '450px' },
            margin: '0 auto',
            borderRadius: { xs: '12px', sm: '8px', md: '8px' },
            bgcolor: '#f0f0f0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
          }}
        >
          {!isDeletedAccount && <Box
            sx={{
              position: "absolute",
              top: 8,
              left: 8,
              px: 1.4,
              py: 0.5,
              bgcolor: "rgba(0,0,0,0.4)",
              color: '#fff',
              borderRadius: "999px",
              display: "flex",
              alignItems: "center",
              letterSpacing: '1px',
              gap: "6px",
              fontSize: "12px",
              fontWeight: 400,
              boxShadow: 1,
              zIndex: 2,
            }}
          >
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                bgcolor: color, // dynamic color
              }}
            />
            {label} {/* dynamic label */}
          </Box>}


          {isMobile && (image || photos.length > 0) ? (
            <Swiper
              pagination={{
                clickable: false,
                dynamicBullets: true,
              }}
              modules={[Pagination, Navigation]}
              spaceBetween={10}
              slidesPerView={1}
              style={{ width: "100%", height: "100%" }}
            >
              {image && (
                <SwiperSlide key="uploaded">
                  <Box sx={{ position: "relative", width: '100%' }}>
                    <a data-fancybox="gallery" href={image} style={{ width: '100%', height: '100%' }}>
                      <CardMedia
                        component="img"
                        image={image}
                        alt="Uploaded"
                        onError={(e) => {
                          if (e.target.src.includes("broken-image.png")) return;
                          e.target.src = "/assets/images/broken-image.png";
                        }}
                        sx={{
                          width: "100%",
                          height: "60vh",
                          objectFit: "cover",
                          borderRadius: "12px",
                          cursor: "pointer",
                        }}
                      />
                    </a>

                    {/* Add this Favorite icon for uploaded image */}
                    <IconButton
                      onClick={() => handleToggleFavorite("uploaded")}
                      sx={{
                        position: "absolute",
                        bottom: 8,
                        right: 8,
                        color: "white",
                        backgroundColor: "rgba(255, 255, 255, 0.7)",
                        "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.9)" },
                      }}
                    >
                      {favorites.includes("uploaded") ? (
                        <FavoriteIcon sx={{ color: "#ff1744" }} />
                      ) : (
                        <FavoriteBorderIcon />
                      )}
                    </IconButton>
                  </Box>
                </SwiperSlide>
              )}

              {photos.map((photo, index) => (
                <SwiperSlide key={index}>
                  <Box sx={{ position: "relative", width: '100%' }}>
                    <a data-fancybox="gallery" href={photo} style={{ width: '100%', height: '100%' }}>
                      <CardMedia
                        component="img"
                        image={photo}
                        alt={photo}
                        sx={{
                          width: "100%",
                          height: "60vh",
                          objectFit: "cover",
                          borderRadius: "12px",
                          cursor: "pointer",
                        }}
                      />
                    </a>

                    {/* Favorite icon */}
                    <IconButton
                      onClick={() => handleToggleFavorite(index)}
                      sx={{
                        position: "absolute",
                        bottom: 8,
                        right: 8,
                        color: "white",
                        backgroundColor: "rgba(255, 255, 255, 0.7)",
                        "&:hover": { backgroundColor: "rgba(0,0,0,0.9)" },
                      }}
                    >
                      {favorites.includes(index) ? (
                        <FavoriteIcon sx={{ color: "#ff1744" }} />
                      ) : (
                        <FavoriteBorderIcon />
                      )}
                    </IconButton>
                  </Box>
                </SwiperSlide>
              ))}
            </Swiper>
          ) : isUploading ? (
            <Box display="flex" flexDirection="column" alignItems="center">
              <CircularProgress
                variant="determinate"
                value={uploadProgress}
                size={isMobile ? 30 : 64}
                thickness={4}
              />
              <Typography mt={1} variant="body2" color="textSecondary">
                {uploadProgress}%
              </Typography>
            </Box>
          ) : image ? (
                <a data-fancybox="gallery" href={image} style={{ width: '100%', height: '100%' }}>
              <CardMedia
                component="img"
                image={image}
                onError={(e) => {
                    if (e.target.src.includes("user.svg")) return;
                    e.target.src = "/assets/images/user-profile/user.svg";
                }}
                // alt="Uploaded"
                sx={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  borderRadius: '8px',
                  cursor: 'pointer'
                }}
              />
            </a>
          ) : (
            <Avatar
              alt="Profile Avatar"
              src="/assets/images/user-profile/user.svg"
              sx={{
                width: '100%',
                height: '100%',
                borderRadius: '50%'
              }}
            />
          )}
        </Box>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileChange}
          style={{ display: 'none' }}
        />
      </Box>


    </>
  );
};

export default ImageReplaceWithPicker;
